<template>
    <div>
        <div class="row mb-5" v-if="isRendered">
            <div class="col-12">
                <div class="d-flex">
                    <div class="flex-grow-1">
                        <p class="text-secondary mb-3" v-show="panel.panel_type == 'multichoice'">
                            <span class="icon icon-glyphicons-basic-directions-arrow"></span>
                            Branching Question
                        </p>
                        <p class="text-secondary mb-3" v-show="panel.panel_type == 'multiselect'">
                            <span class="icon icon-glyphicons-basic-square-empty-check"></span>
                            Multiple Selection Inquiry
                        </p>
                        <p class="text-secondary mb-3" v-show="panel.panel_type == 'singleselect'">
                            <span class="icon icon-glyphicons-basic-circle-selected"></span>
                            Multiple Choice Inquiry
                        </p>
                        <p class="text-secondary mb-3" v-show="panel.panel_type == 'image_hotspots'">
                            <span class="icon icon-glyphicons-basic-target"></span>
                            Image Hotspots
                        </p>
                        <p class="mb-4">
                            <router-link v-if="!disableEditorLinks" :to="nodeAuthorLink"
                                >Screen {{ panel.node.number }} - {{ panel.node.title }}</router-link
                            >
                            <span v-else>Screen {{ panel.node.number }} - {{ panel.node.title }}</span>
                        </p>
                        <p class="h5 mb-1">{{ panel.reporting_label }}</p>
                        <p class="mb-2">
                            <span class="text-secondary small" v-show="panel.reporting_label != panel.header_content">{{ panel.header_content }}</span>
                        </p>
                    </div>
                    <div class="flex-grow-0 flex-shrink-0">
                        <div class="mb-0">
                            <form method="GET" action="/api/reports/choice/export/">
                                <input
                                    v-for="param in reportParams"
                                    :key="param"
                                    hidden
                                    style="display: none"
                                    :name="param"
                                    :value="savedReportQuery[param]"
                                />
                                <button class="btn btn-light btn-sm" type="submit">
                                    <span class="icon icon-glyphicons-basic-square-download mr-1"></span>Export
                                    Data
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="row mb-5" v-if="choiceReport.choices.length" v-show="choiceReport.num_responders > 0">
            <div class="col-12">
                <p class="h6 mb-3">
                    Inquiry Responses
                    <span class="badge badge-gray ml-1">{{ choiceReport.num_responders }} Learners</span>
                </p>
            </div>
            <div class="col-12 col-lg-10 col-xl-7 mb-3">
                <ul class="list-group">
                    <li class="list-group-item" v-for="(choice, index) in choiceReport.choices" :key="choice.id">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1">
                                <p class="mb-0 choice-text">
                                    {{ index + 1 }}. {{ choice.text }}
                                    <span
                                        v-show="choice.correct"
                                        class="icon icon-glyphicons-basic-circle-empty-check text-success"
                                    ></span>
                                </p>
                            </div>
                            <div class="flex-shrink-0 pl-3">
                                <span class="text-primary mr-1">
                                    {{ choice.num_responders }}
                                </span>
                                <span class="text-secondary"> ({{ choice.responder_percentage }}%) </span>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
            <div class="col-12">
                <p class="small text-secondary mb-0" style="margin-left: 2px">
                    Note: Repeat learner attempts are excluded from this report.
                </p>
            </div>
        </div>
        <div class="row" v-show="choiceReport.num_responders > 0">
            <div class="col-12 col-xl-12">
                <div id="choice-chart" class="mt-2" style="height: 400px"></div>
            </div>
        </div>
        <div class="row mt-4">
            <div class="col-12">
                <p class="text-secondary mb-2" v-show="choiceReport.num_responders == 0">
                    There aren't any responses to this inquiry yet.
                </p>
                <p class="text-secondary" v-show="choiceReport.panel && choiceReport.choices.length == 0">
                    No choices have been configured on this inquiry yet.
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import { BarChart } from "chartist"
import http from "@/axios.js"

export default {
    name: "ChoiceResponseReport",
    props: {
        panelId: {
            type: Number,
            required: false,
        },
        libraryItemId: {
            type: String,
            required: false,
            default: null,
        },
        startDate: {
            type: String,
            required: false,
            default: null,
        },
        endDate: {
            type: String,
            required: false,
            default: null,
        },
        firstResponseOnly: {
            type: Boolean,
            required: false,
            default: false,
        },
        pageId: {
            type: Number,
            required: false,
            default: null,
        },
        shareLinkId: {
            type: Number,
            required: false,
            default: null,
        },
        includeShareLinks: {
            type: Boolean,
            required: false,
            default: true,
        },
        launchRequestId: {
            type: Number,
            required: false,
            default: null,
        },
        // When true, links to the case editor view are hidden.
        disableEditorLinks: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            panel: {
                node: {},
            },
            choiceReport: {
                choices: [],
            },
            reportParams: [
                "libraryItemId",
                "panelId",
                "startDate",
                "endDate",
                "shareLinkId",
                "launchRequestId",
                "includeShareLinks",
            ],
            savedReportQuery: {},
            isRendered: false,
        }
    },
    computed: {
        nodeAuthorLink() {
            return `/library/${this.panel.node.case_id}/nodes/${this.panel.node.id}/`
        },
    },
    methods: {
        refresh() {
            let vm = this
            this.$store.dispatch("addLoad")

            // Store the query parameters associated with the report being fetched. These are
            // used if the user exports the data associated with the report being viewed.
            this.savedReportQuery = {
                libraryItemId: this.libraryItemId,
                panelId: this.panelId,
                startDate: this.startDate,
                endDate: this.endDate,
                firstResponseOnly: this.firstResponseOnly,
                includeShareLinks: this.includeShareLinks,
                pageId: this.pageId,
                launchRequestId: this.launchRequestId,
                shareLinkId: this.shareLinkId,
            }

            http.get("/api/reports/choice/", {
                params: this.savedReportQuery,
            })
                .then(function (response) {
                    vm.isRendered = true
                    vm.panel = response.data.panel
                    vm.choiceReport = response.data
                    vm.$nextTick(() => vm.renderChart())
                })
                .finally(function () {
                    vm.$store.dispatch("removeLoad")
                })
        },
        renderChart() {
            this.choiceReport.choices.forEach((choice, index) => (choice.index = index + 1))
            var data = {
                labels: this.choiceReport.choices.map((choice) => choice.index + ". " + choice.text),
                series: [this.choiceReport.choices.map((choice) => choice.num_responders)],
            }
            let options = {
                axisY: {
                    onlyInteger: true,
                },
            }
            new BarChart("#choice-chart", data, options)
        },
    },
}
</script>

<style lang="scss">
@import "@/assets/styles/variables.scss";
@import "chartist/dist/index.scss";

.ct-series-a .ct-bar {
    stroke: $primary;
    stroke-width: 20px;
}

#choice-chart {
    svg {
        overflow: visible;
    }
}

.ct-label {
    margin-top: 6px;
}

.choice-text {
    font-size: 15px;
}
</style>
